import type { IBraintree } from "@/types/PaymentGatewayTypes.ts";
import axios from 'axios';
import { ApiPost } from '@/api/Common.ts';
import type { AxiosError } from 'axios';
import { trackAddPaymentInfo } from '@/analytics/index'

declare const braintree: IBraintree;

class AoeBraintreeAPM {
  clientToken = '';
  container = '';
  nonce = '';
  braintreeScriptUrl = 'https://js.braintreegateway.com/web/3.129.0/js/client.min.js';
  braintreeApmScriptUrl = 'https://js.braintreegateway.com/web/3.129.0/js/local-payment.min.js';
  createPaymentUrl = '';
  merchantAccountId = 'tpcaustriaEUR'; // Default merchant account ID
  customerEmail = 'customer@example.com'; // Default customer email
  customerFirstName = '';
  customerLastName = '';
  cartFunnelType: 'individual' | 'party' | 'recruitment' = 'individual';
  sourceValues = {
    systemKeyword: '',
    aOECartIdToken: '',
  };
  paymentValues = {
    paymentAmount: '',
    currencyCode: '',
  };

  Initialize(): void {
    console.log('Braintree APM', 'Initialize');

    // Load Braintree scripts with error handling
    this.loadScripts()
      .then(() => {
        console.log('Braintree APM', 'Scripts loaded');
        this.InitializeDocumentReady();
      })
      .catch((error) => {
        console.error('Error loading Braintree scripts:', error);
      });
  }

  private async loadScripts(): Promise<void> {
    try {
      await axios.get(this.braintreeScriptUrl);
      console.log('Braintree client script loaded');

      await axios.get(this.braintreeApmScriptUrl);
      console.log('Braintree APM script loaded');

      return Promise.resolve();
    } catch (error) {
      console.error('Error loading scripts:', error);
      return Promise.reject(error);
    }
  }

  InitializeDocumentReady(): void {
    console.log('Braintree APM', 'InitializeDocumentReady');

    try {
      // Check if braintree is available
      if (typeof braintree === 'undefined') {
        console.error('Braintree library not loaded. APM integration will not work.');
        return;
      }

      // Create a client
      braintree.client.create(
        {
          authorization: this.clientToken,
        },
        (clientErr: any, clientInstance: any) => {
          if (clientErr) {
            console.error('Error creating Braintree client:', clientErr);
            return;
          }

          // Create APM component
          braintree.localPayment.create(
            {
              client: clientInstance
            },
            (apmErr: any, apmInstance: any) => {
              if (apmErr) {
                console.error('Error creating APM instance:', apmErr);
                return;
              }

              // Setup EPS payment method
              this.setupEpsPayment(apmInstance);
            }
          );
        }
      );
    } catch (error) {
      console.error('Error initializing Braintree APM:', error);
    }
  }

  setupEpsPayment(apmInstance: any): void {
    // Create container for EPS button if it doesn't exist
    const container = document.getElementById('braintree-eps-button');
    if (!container) {
      console.error('EPS button container not found');
      return;
    }

    // Clear any existing content
    container.innerHTML = '';

    // Create EPS button
    const button = document.createElement('button');
    button.id = 'eps-button';
    button.className = 'btn k-button k-button-md k-button-solid k-button-solid-primary';
    button.textContent = 'Pay with EPS';
    button.addEventListener('click', () => this.handleEpsPayment(apmInstance));

    container.appendChild(button);
  }

  handleEpsPayment(apmInstance: any): void {
    // Configuration for EPS payment
    const paymentOptions = {
      flow: 'checkout',
      amount: this.paymentValues.paymentAmount,
      currencyCode: 'EUR', // EPS only supports EUR
      displayName: 'Thatcher Technology Group',
      paymentType: 'eps',
      paymentTypeCountryCode: 'AT', // Austria
      merchantAccountId: this.merchantAccountId, // Use the class property
      email: this.customerEmail, // Use customer email from cart
      givenName: this.customerFirstName,
      surname: this.customerLastName,
      fallback: {
        url: window.location.href,
        buttonText: 'Return to Merchant'
      },
      onPaymentStart: (data: any, start: Function) => {
        console.log('EPS payment started:', data);
        // You can perform validation here before starting the payment
        start();
      }
    };

    apmInstance.startPayment(paymentOptions, (startPayErr: any, payload: any) => {
      if (startPayErr) {
        console.error('Error starting EPS payment:', startPayErr);
        return;
      }

      const modifiedPayload = {
        ...payload,
        type: 'eps'
      };

      // Payment was successful, send the payment data to your server
      console.log('EPS payment successful:', modifiedPayload);
      this.createPayment(modifiedPayload);
    });
  }

  createPayment(payload: any): void {
    console.debug('SavePayment', payload);

    const jsonDataToSave = {
      systemKeyword: this.sourceValues.systemKeyword,
      aOECartIdToken: this.sourceValues.aOECartIdToken,
      BraintreeAPMTokenizeResponse: {
        ...payload,
        clientToken: this.clientToken,
      },
      charge: Number(this.paymentValues.paymentAmount),
    };

    if (this.createPaymentUrl && this.createPaymentUrl.length > 0) {
      ApiPost(this.createPaymentUrl, jsonDataToSave)
        .then((result: any) => {
          console.log('Processing complete', result);
          trackAddPaymentInfo(
            'OPA',
            Number(this.paymentValues.paymentAmount) || 0,
            this.paymentValues.currencyCode || 'EUR',
            this.cartFunnelType || 'individual'
          )
          const successMessage = {
            messageType: 'aoepaymentcreated',
            messageData: result,
          };
          window.postMessage(successMessage);
        })
        .catch((errorResult: any) => {
          console.log('Processing error', errorResult);
          const axiosError = errorResult as AxiosError;
          let messageData = null;
          if (axiosError) {
            messageData = axiosError.response?.data;
          }
          const errorMessage = {
            messageType: 'aoepaymentcreationerror',
            messageData: messageData,
          };
          window.postMessage(errorMessage);
        });
    } else {
      console.error('createPaymentUrl is empty');
    }
  }
}

export const aoeBraintreeAPM = new AoeBraintreeAPM();
